import { startCase } from "lodash";
import { FC, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAreaTitle } from "@ryde/redux";
import { NO_VALUE_DISPLAY, TNeighborAreaSettingsState } from "@ryde/shared";

import { FramerTooltip } from "../../../common";
import { ColorIconButton } from "../../../controls";
import { useNeighborAreasTemplateSettings } from "./hooks";
import { NeighborAreasSettingsItemEdit } from "./neighbor-areas-settings-item-edit";
import { NeighborAreasTemplateContext } from "./neighbor-areas-template-context";

interface INeighborAreasTemplateSettingsItemProps {
  neighborAreaDetails: TNeighborAreaSettingsState;
  neighborAreaDetailsIndex: number;
}

export const NeighborAreasSettingsItem: FC<
  INeighborAreasTemplateSettingsItemProps
> = ({ neighborAreaDetails, neighborAreaDetailsIndex }) => {
  const { t } = useTranslation();
  const [isEdit, setIsEdit] = useState(false);
  const {
    getAndSetDayTypesLabels,
    getAndSetDayGroupsLabels,
    getAndSetSitesLabels,
    dayGroupsLabels,
    dayTypesLabels,
    sitesLabels,
  } = useNeighborAreasTemplateSettings();
  const fromTimeLabel = neighborAreaDetails?.fromTime ?? NO_VALUE_DISPLAY;
  const toTimeLabel = neighborAreaDetails?.toTime ?? NO_VALUE_DISPLAY;
  const directionLabel = neighborAreaDetails?.direction ?? NO_VALUE_DISPLAY;
  const maxSeatsLabel = neighborAreaDetails?.maxAreaSeats ?? NO_VALUE_DISPLAY;
  const onEdit = (): void => setIsEdit(true);
  const onCancelEdit = (): void => setIsEdit(false);
  const { onRemove, onInnerEditStart, onInnerEditEnd } = useContext(
    NeighborAreasTemplateContext,
  );

  useEffect(() => {
    if (isEdit) {
      onInnerEditStart();
    } else {
      onInnerEditEnd();
    }
  }, [isEdit, onInnerEditStart, onInnerEditEnd]);
  const { title: areaLabel } = useAreaTitle(neighborAreaDetails.areaId);

  useEffect(() => {
    if (neighborAreaDetails?.dayTypeIds?.length) {
      getAndSetDayTypesLabels(neighborAreaDetails.dayTypeIds);
    }

    if (neighborAreaDetails?.dayGroupIds?.length) {
      getAndSetDayGroupsLabels(neighborAreaDetails.dayGroupIds);
    }

    if (neighborAreaDetails?.siteIds?.length) {
      getAndSetSitesLabels(neighborAreaDetails.siteIds);
    }
  }, [
    getAndSetDayGroupsLabels,
    getAndSetDayTypesLabels,
    getAndSetSitesLabels,
    neighborAreaDetails.areaId,
    neighborAreaDetails.dayGroupIds,
    neighborAreaDetails.dayTypeIds,
    neighborAreaDetails.siteIds,
  ]);

  if (isEdit) {
    return (
      <NeighborAreasSettingsItemEdit
        neighborAreaDetails={neighborAreaDetails}
        onCancelEdit={onCancelEdit}
        neighborAreaDetailsIndex={neighborAreaDetailsIndex}
      />
    );
  }

  return (
    <div
      className="NeighborAreasSettings-Item display-mode"
      title={`${areaLabel}(ID:${neighborAreaDetails.areaId})`}
    >
      <div>{areaLabel}</div>
      <div>{fromTimeLabel}</div>
      <div>{toTimeLabel}</div>
      <div>{directionLabel}</div>
      <div className="NeighborAreasSettings-ItemDayGroups truncate-text">
        <FramerTooltip
          content={`${startCase(t("dayGroup.plural"))}: ${dayGroupsLabels}`}
        >
          {dayGroupsLabels}
        </FramerTooltip>
      </div>
      <div className="NeighborAreasSettings-ItemDayTypes truncate-text">
        <FramerTooltip
          content={`${startCase(t("dayType.plural"))}: ${dayTypesLabels}`}
        >
          {dayTypesLabels}
        </FramerTooltip>
      </div>

      {Boolean(neighborAreaDetails?.siteIds?.length) && (
        <div className="NeighborAreasSettings-ItemSites truncate-text">
          <FramerTooltip
            content={`${startCase(t("site.plural"))}: ${sitesLabels}`}
          >
            {sitesLabels}
          </FramerTooltip>
        </div>
      )}

      <div className="NeighborAreasSettings-Buttons">
        <ColorIconButton iconType="EDIT" onClick={onEdit} />
        <ColorIconButton
          iconType="DELETE"
          onClick={() => onRemove(neighborAreaDetails.tmpId)}
        />
      </div>
      {neighborAreaDetails?.maxAreaSeats && (
        <div className="NeighborAreasSettings-MaxSeats">
          <span className="label">
            {startCase(t("area.formLabel.maxAreaSeats"))}
          </span>
          <span className="value">{maxSeatsLabel}</span>
        </div>
      )}
    </div>
  );
};
